/**
 * LagerPickerModal (web) — reusable "pick from Lager" pop-out.
 * Searches the warehouse catalog with a system-chip filter and a per-row
 * quantity stepper, then hands each pick back via onAdd. Catalog-only.
 *
 * Props:
 *  - open (bool)
 *  - onClose ()
 *  - authHeader () → axios config with Bearer token
 *  - selected ({ [item_id]: qty }) — already-picked quantities
 *  - onAdd ({ item_id, name, quantity, unit }) — called per add; keeps open
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { X, Search, Package, Plus, Minus } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LagerPickerModal({ open, onClose, authHeader, selected = {}, onAdd }) {
  const [catalog, setCatalog] = useState([]);
  const [q, setQ] = useState("");
  const [sys, setSys] = useState("");
  const [qty, setQty] = useState({});

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/doserne/lager/catalog`, authHeader());
        if (!cancelled) setCatalog(data.items || []);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [open, authHeader]);

  const bQty = (id) => Math.max(1, Number(qty[id]) || 1);
  const setBQty = (id, v) => setQty((m) => ({ ...m, [id]: Math.max(1, Number(v) || 1) }));

  const systems = Array.from(new Set(catalog.map((c) => (c.system || "").trim()).filter(Boolean))).sort();
  const results = (() => {
    const needle = q.trim().toLowerCase();
    let base = catalog;
    if (sys) base = base.filter((c) => (c.system || "").trim() === sys);
    if (needle) base = base.filter((c) => `${c.name} ${c.sku || ""} ${c.art_no || ""} ${c.system || ""} ${c.category || ""}`.toLowerCase().includes(needle));
    return base.slice(0, 60);
  })();

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="lagerpick-backdrop">
      <div className="bg-white w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="lagerpick">
        <div className="px-4 py-3 border-b border-slate-200 bg-cyan-50 flex items-center gap-2">
          <Package className="w-4 h-4 text-cyan-700" />
          <div className="text-sm font-black text-cyan-800 flex-1">Adaugă din Lager</div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900" data-testid="lagerpick-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-3 border-b border-slate-100">
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Caută articol, cod, sistem…" className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm text-slate-900 focus:border-cyan-500 focus:outline-none" data-testid="lagerpick-search" />
          </div>
        </div>
        {systems.length > 0 && (
          <div className="px-3 py-2 flex items-center gap-1.5 overflow-x-auto border-b border-slate-100">
            <button type="button" onClick={() => setSys("")} className={`flex-shrink-0 px-2.5 py-1 rounded-full text-[11px] font-black border transition ${!sys ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`} data-testid="lagerpick-sys-all">Toate</button>
            {systems.map((s) => (
              <button key={s} type="button" onClick={() => setSys((cur) => cur === s ? "" : s)} className={`flex-shrink-0 px-2.5 py-1 rounded-full text-[11px] font-black border transition ${sys === s ? "bg-cyan-600 text-white border-cyan-600" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`} data-testid={`lagerpick-sys-${s}`}>{s}</button>
            ))}
          </div>
        )}
        <div className="flex-1 overflow-y-auto">
          {results.length === 0 ? (
            <div className="py-10 text-center text-sm text-slate-400" data-testid="lagerpick-empty">Niciun articol.</div>
          ) : results.map((c) => {
            const inList = selected[c.id];
            return (
              <div key={c.id} className="px-4 py-2.5 border-b border-slate-100 flex items-center gap-2" data-testid={`lagerpick-opt-${c.id}`}>
                <span className="flex-1 min-w-0">
                  <span className="text-sm font-bold text-slate-800 truncate block">{c.name}</span>
                  <span className="text-[10px] text-slate-400">{[c.system, c.category, c.art_no || c.sku].filter(Boolean).join(" · ")}</span>
                </span>
                <span className={`text-[10px] font-black px-1.5 py-0.5 rounded flex-shrink-0 ${c.available < 1 ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}>{c.available} {c.unit}</span>
                {inList ? <span className="text-[10px] font-black text-cyan-700 bg-cyan-100 rounded-full px-2 py-0.5 flex-shrink-0" data-testid={`lagerpick-inlist-${c.id}`}>×{inList}</span> : null}
                <div className="flex items-center gap-0.5 flex-shrink-0">
                  <button type="button" onClick={() => setBQty(c.id, bQty(c.id) - 1)} className="w-6 h-6 rounded border border-slate-200 text-slate-500 flex items-center justify-center hover:bg-slate-100"><Minus className="w-3 h-3" /></button>
                  <input type="number" min={1} value={bQty(c.id)} onChange={(e) => setBQty(c.id, e.target.value)} className="w-9 text-center text-xs border border-slate-200 rounded py-0.5 text-slate-900" data-testid={`lagerpick-qty-${c.id}`} />
                  <button type="button" onClick={() => setBQty(c.id, bQty(c.id) + 1)} className="w-6 h-6 rounded border border-slate-200 text-slate-500 flex items-center justify-center hover:bg-slate-100"><Plus className="w-3 h-3" /></button>
                </div>
                <button type="button" onClick={() => onAdd({ item_id: c.id, name: c.name, quantity: bQty(c.id), unit: c.unit || "stk" })} className="flex-shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white text-xs font-black" data-testid={`lagerpick-add-${c.id}`}>
                  <Plus className="w-3.5 h-3.5" /> Adaugă
                </button>
              </div>
            );
          })}
        </div>
        <div className="px-4 py-2.5 border-t border-slate-100 bg-slate-50 flex justify-end">
          <button onClick={onClose} className="px-4 py-1.5 rounded-lg bg-slate-900 text-white text-sm font-semibold" data-testid="lagerpick-done">Gata</button>
        </div>
      </div>
    </div>
  );
}
